"use client";
/**
 * Client-side Supabase instance for the landings admin.
 * Same URL + anon key as landingsData.ts (server-side), but constructed with
 * supabase-js so auth/sessions work client-side.
 */
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL =
  process.env.NEXT_PUBLIC_LANDINGS_SUPABASE_URL || "https://landings-api.hirebee.ai";
const ANON_KEY =
  process.env.NEXT_PUBLIC_LANDINGS_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsImlzcyI6InN1cGFiYXNlIiwiaWF0IjoxNzgxNTgzNzc4LCJleHAiOjIwOTY5NDM3Nzh9.-8S5sHtUHCXVSWK1xkSpjm7rbKRzjlYQAW8Ngtc1Dvs";

// postgrest-js >= 2.110 stamps `X-Retry-Count` on every retried request (PostgrestBuilder,
// once attemptCount > 0). The gateway's CORS `Access-Control-Allow-Headers` does not list
// it, so the browser rejects the preflight and *every* SDK retry dies as a CORS error —
// which is how a plain 502 or a dropped connection ends up reported as a CORS failure with
// the real cause invisible. Strip the header until the gateway allowlist is fixed
// (CLAUDE.md → "Open infra asks"). Remove this deliberately once `x-retry-count` is
// allowed there — not as a tidy-up.
function withoutRetryCount(init?: RequestInit): RequestInit | undefined {
  const headers = init?.headers;
  if (!headers) return init;

  if (headers instanceof Headers) {
    const copy = new Headers(headers);
    copy.delete("x-retry-count");
    return { ...init, headers: copy };
  }
  if (Array.isArray(headers)) {
    return { ...init, headers: headers.filter(([key]) => key.toLowerCase() !== "x-retry-count") };
  }
  // In practice supabase-js normalises to a Headers instance before we see it, but
  // postgrest-js builds a plain object and auth-js/functions-js also route through here.
  const copy: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() !== "x-retry-count") copy[key] = value;
  }
  return { ...init, headers: copy };
}

const fetchWithoutRetryCount: typeof fetch = (input, init) => fetch(input, withoutRetryCount(init));

// Only one client instance per process.
let _client: ReturnType<typeof createClient> | null = null;

export function getSupabaseClient() {
  if (!_client) {
    _client = createClient(SUPABASE_URL, ANON_KEY, {
      global: { fetch: fetchWithoutRetryCount },
      auth: {
        storage: typeof window !== "undefined" ? window.localStorage : undefined,
        persistSession: true,
        autoRefreshToken: true,
      },
    });
  }
  return _client;
}

export const supabase = {
  get auth() {
    return getSupabaseClient().auth;
  },
  from: (...args: Parameters<ReturnType<typeof createClient>["from"]>) =>
    getSupabaseClient().from(...args),
  rpc: (...args: Parameters<ReturnType<typeof createClient>["rpc"]>) =>
    getSupabaseClient().rpc(...args),
  get functions() {
    return getSupabaseClient().functions;
  },
  storage: {
    get from() {
      return getSupabaseClient().storage.from.bind(getSupabaseClient().storage);
    },
  },
  channel: (...args: Parameters<ReturnType<typeof createClient>["channel"]>) =>
    getSupabaseClient().channel(...args),
  removeChannel: (...args: Parameters<ReturnType<typeof createClient>["removeChannel"]>) =>
    getSupabaseClient().removeChannel(...args),
  getChannels: () => getSupabaseClient().getChannels(),
};
